import { computed, effectScope, ref, type ComputedRef, type EffectScope, type Ref } from "vue";
import { useLocalStorage, type RemovableRef } from "@vueuse/core";

interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

interface NavigatorLike {
    userAgent: string;
    platform: string;
    maxTouchPoints: number;
    standalone?: boolean;
}

/*****************************************************************************
 * UX roadmap #104: installing the app as a lifecycle moment.
 *
 * The browser's own install prompt is held back (beforeinstallprompt is
 * preventDefault-ed) and was offered only as a Support menu item. The install
 * card (PwaInstallCard.vue) now offers it once a member has come back a few
 * times, and "Not now" snoozes it for 30 days.
 *
 * iOS has no prompt: Safari, and since iOS 16.4 Chrome and Edge, add to the
 * Home Screen from their Share menu, so the card opens the instructions for
 * the browser in use. In-app browsers (Instagram, Facebook, the Google app...)
 * cannot install at all; they get "Open in Safari" / "Open in browser".
 *
 * The visit count and the snooze are device state, not member data, so they
 * are not in USER_SCOPED_PURGE_KEYS.
 *****************************************************************************/

export const PWA_INSTALL_VISITS_STORAGE_KEY = "pwa-install-visits";
export const PWA_INSTALL_DISMISSED_AT_STORAGE_KEY = "pwa-install-dismissed-at";

/** The card waits for the member's third visit (page load) on this device. */
export const InstallCardMinVisits = 3;
/** "Not now", or closing the browser's own prompt, hides the card for this long. */
export const InstallCardSnoozeMilliseconds = 30 * 24 * 60 * 60 * 1000;
/** How long into a visit the card waits, so it never lands on a page that is still loading. */
export const InstallCardDelayMilliseconds = 15_000;

/** How the app can be installed here: the browser's prompt, iOS Share-menu steps, or not until opened in a browser. */
export type InstallOffer = "prompt" | "ios" | "in-app";

/** The iOS browsers whose Share menu has "Add to Home Screen". */
export type IosInstallBrowser = "safari" | "chrome" | "edge";

/** Where "Open in browser" leads from an in-app browser: Safari on iOS, the default browser on Android. */
export type InAppBrowserPlatform = "ios" | "android";

const deferredPrompt = ref< BeforeInstallPromptEvent | null >( null );
const canInstall = computed< boolean >( () => deferredPrompt.value !== null );

window.addEventListener( "beforeinstallprompt", ( event: Event ) => {
    event.preventDefault();
    deferredPrompt.value = event as BeforeInstallPromptEvent;
} );

window.addEventListener( "appinstalled", () => {
    deferredPrompt.value = null;
} );

function isIosDevice( nav: NavigatorLike ): boolean {
    return /iPhone|iPad|iPod/.test( nav.userAgent )
        || ( nav.platform === "MacIntel" && nav.maxTouchPoints > 1 );
}

// Apps that open links in their own web view. GSA is the Google app.
const IN_APP_BROWSER = /FBAN|FBAV|FB_IAB|Instagram|LinkedInApp|Twitter|MicroMessenger|Line\/|Snapchat|musical_ly|BytedanceWebview|Pinterest|GSA\//;

/**
 * An in-app browser, which can neither show an install prompt nor add to the Home Screen. Besides the known app tokens:
 * an Android WebView marks itself `; wv)`, and an iOS web view has no `Safari/` token (Safari, Chrome and Edge all do).
 * The installed app's own window has no `Safari/` token either, so check detectStandalone first.
 */
export function detectInAppBrowser( nav: NavigatorLike = navigator as NavigatorLike ): boolean {
    const ua = nav.userAgent;
    if ( IN_APP_BROWSER.test( ua ) ) return true;
    if ( /Android/.test( ua ) && /; wv\)/.test( ua ) ) return true;
    return isIosDevice( nav ) && !/Safari\//.test( ua );
}

// Chrome and Edge on iOS gained "Add to Home Screen" with iOS 16.4. An iPad asking for desktop sites reports a Mac UA
// with no iOS version; that is iPadOS 13 or later, so let it through.
function hasThirdPartyHomeScreenSupport( ua: string ): boolean {
    const version = /(?:iPhone|CPU) OS (\d+)_(\d+)/.exec( ua );
    if ( !version ) return true;
    const [ major, minor ] = [ Number( version[ 1 ] ), Number( version[ 2 ] ) ];
    return major > 16 || ( major === 16 && minor >= 4 );
}

/** The iOS browser whose Share menu can add the app to the Home Screen, or null (not iOS, an in-app browser, Firefox, Opera). */
export function detectIosInstallBrowser( nav: NavigatorLike = navigator as NavigatorLike ): IosInstallBrowser | null {
    if ( !isIosDevice( nav ) || detectInAppBrowser( nav ) ) return null;

    const ua = nav.userAgent;
    if ( /CriOS/.test( ua ) ) return hasThirdPartyHomeScreenSupport( ua ) ? "chrome" : null;
    if ( /EdgiOS/.test( ua ) ) return hasThirdPartyHomeScreenSupport( ua ) ? "edge" : null;
    if ( /FxiOS|OPiOS|Chrome/.test( ua ) ) return null;
    return "safari";
}

export function detectIosSafari( nav: NavigatorLike = navigator as NavigatorLike ): boolean {
    return "safari" === detectIosInstallBrowser( nav );
}

export function detectStandalone(
    nav: NavigatorLike = navigator as NavigatorLike,
    win: Window = window
): boolean {
    if ( nav.standalone === true ) return true;
    return win.matchMedia( "(display-mode: standalone)" ).matches;
}

function inAppBrowserPlatform( nav: NavigatorLike ): InAppBrowserPlatform | null {
    if ( isIosDevice( nav ) ) return "ios";
    return /Android/.test( nav.userAgent ) ? "android" : null;
}

/**
 * A link that leaves an in-app browser for the real one, at the same page: `x-safari-https://…` opens Safari
 * (iOS 17+), an `intent://` URL without a package lets Android pick the default browser. Null elsewhere.
 */
export function openInBrowserUrl( pageUrl: string, nav: NavigatorLike = navigator as NavigatorLike ): string | null {
    let url: URL;
    try {
        url = new URL( pageUrl );
    } catch {
        return null;
    }
    if ( "https:" !== url.protocol && "http:" !== url.protocol ) return null;

    switch ( inAppBrowserPlatform( nav ) ) {
        case "ios":
            return `x-safari-${ url.href }`;
        case "android":
            return `intent://${ url.host }${ url.pathname }${ url.search }#Intent;scheme=${ url.protocol.slice( 0, -1 ) };end`;
        default:
            return null;
    }
}

/** Whether the card is due: enough visits, and no "Not now" within the snooze. */
export function isInstallCardDue( visits: number, dismissedAt: number | null, now: number ): boolean {
    if ( visits < InstallCardMinVisits ) return false;
    return null === dismissedAt || now - dismissedAt >= InstallCardSnoozeMilliseconds;
}

const isStandalone = detectStandalone();
const iosInstallBrowser = isStandalone ? null : detectIosInstallBrowser();
const inAppPlatform = !isStandalone && detectInAppBrowser() ? inAppBrowserPlatform( navigator as NavigatorLike ) : null;
const canShowIosInstall = computed< boolean >( () => null !== iosInstallBrowser );
const canOpenInBrowser = null !== inAppPlatform && null !== openInBrowserUrl( window.location.href );

const installOffer = computed< InstallOffer | null >( () => {
    if ( isStandalone ) return null;
    if ( canInstall.value ) return "prompt";
    if ( canShowIosInstall.value ) return "ios";
    return canOpenInBrowser ? "in-app" : null;
} );

const showIosInstructions = ref< boolean >( false );

function openIosInstallInstructions(): void {
    showIosInstructions.value = true;
}

function closeIosInstallInstructions(): void {
    showIosInstructions.value = false;
}

// Created on first use so that importing the module touches no storage. VueUse catches a blocked or full storage
// itself; the values then hold for this page load only. The refs live in a detached scope: created inside the first
// card's setup, their write-back would stop when that card unmounts (sign-out) and a later "Not now" would not be kept.
let storageScope: EffectScope | undefined;
let visits: RemovableRef< number > | undefined;
let dismissedAt: RemovableRef< number | null > | undefined;
let visitCounted = false;

function inStorageScope< T >( create: () => T ): T {
    storageScope ??= effectScope( true );
    return storageScope.run( create )!;
}

function visitsRef(): RemovableRef< number > {
    visits ??= inStorageScope( () => useLocalStorage< number >( PWA_INSTALL_VISITS_STORAGE_KEY, 0, { onError: () => {} } ) );
    return visits;
}

function toTimestamp( raw: string ): number | null {
    const value = Number( raw );
    return "" !== raw && Number.isFinite( value ) ? value : null;
}

function dismissedAtRef(): RemovableRef< number | null > {
    dismissedAt ??= inStorageScope( () => useLocalStorage< number | null >( PWA_INSTALL_DISMISSED_AT_STORAGE_KEY, null, {
        serializer: { read: toTimestamp, write: String },
        writeDefaults: false,
        onError: () => {}
    } ) );
    return dismissedAt;
}

function snoozeInstallCard(): void {
    dismissedAtRef().value = Date.now();
}

/** The browser's install prompt. It can be shown once, so the held event is dropped whatever the member chooses. */
async function installPwa(): Promise< void > {
    const prompt = deferredPrompt.value;
    if ( !prompt ) return;

    deferredPrompt.value = null;
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;

    // Chrome itself holds back its prompt for a while after a refusal; the card waits as long as "Not now" would.
    if ( outcome === "dismissed" ) {
        snoozeInstallCard();
    }
}

/** Leaves an in-app browser for Safari or the default browser, at the page the member is on. */
function openInBrowser(): void {
    const target = openInBrowserUrl( window.location.href );
    if ( target ) window.location.assign( target );
}

export function usePwaInstall() {
    return {
        canInstall,
        installPwa,
        canShowIosInstall,
        iosInstallBrowser,
        inAppPlatform,
        installOffer,
        openInBrowser,
        showIosInstructions,
        openIosInstallInstructions,
        closeIosInstallInstructions
    };
}

export interface InstallCardState {
    installOffer: ComputedRef< InstallOffer | null >;
    /** The member has visited often enough and has not said "Not now" in the last 30 days. */
    isDue: ComputedRef< boolean >;
    dismiss: () => void;
    visits: Ref< number >;
}

/**
 * The install card's state. The first call in a page load counts one visit; call it from the member shell only, so
 * visits mean a signed-in member coming back.
 */
export function useInstallCard(): InstallCardState {
    const count = visitsRef();
    if ( !visitCounted ) {
        visitCounted = true;
        count.value = ( Number.isFinite( count.value ) ? count.value : 0 ) + 1;
    }
    const snoozedAt = dismissedAtRef();

    return {
        installOffer,
        isDue: computed( () => isInstallCardDue( count.value, snoozedAt.value, Date.now() ) ),
        dismiss: snoozeInstallCard,
        visits: count
    };
}

/** For specs: forget the counted visit and the storage refs, so the next useInstallCard() reads storage again. */
export function resetInstallCardState(): void {
    storageScope?.stop();
    storageScope = undefined;
    visits = undefined;
    dismissedAt = undefined;
    visitCounted = false;
}
